import { Injectable } from '@angular/core';
import posthog from 'posthog-js';

export enum FeatureFlags {
  DISPLAY_TOPICS = 'displayTopics',
  DISPLAY_TOURS = 'displayTours',
  MASTER_BUTTON = 'masterButton',
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private readonly FEATURE_FLAGS_DISABLED_KEY = 'featureFlags_disabled';
  private readonly FEATURE_FLAGS_INITIALIZED_KEY = 'featureFlags_initialized';

  constructor() {
    this.initializeFeatureFlags();
  }

  /**
   * Vérifie si PostHog est disponible
   */
  private isPostHogAvailable(): boolean {
    return typeof posthog !== 'undefined' && posthog !== null && typeof posthog.isFeatureEnabled === 'function';
  }

  /**
   * Initialise les feature flags au premier chargement du site
   */
  private initializeFeatureFlags(): void {
    const isInitialized = localStorage.getItem(this.FEATURE_FLAGS_INITIALIZED_KEY);

    if (!isInitialized) {
      // Premier chargement du site → désactiver par défaut
      localStorage.setItem(this.FEATURE_FLAGS_DISABLED_KEY, 'true');
      localStorage.setItem(this.FEATURE_FLAGS_INITIALIZED_KEY, 'true');
      console.log('🔧 Premier chargement détecté - Feature flags désactivées par défaut');
    }
  }

  /**
   * Vérifie si les feature flags sont désactivés globalement par l'utilisateur
   */
  private areFeatureFlagsDisabled(): boolean {
    const disabled = localStorage.getItem(this.FEATURE_FLAGS_DISABLED_KEY);
    return disabled === 'true';
  }

  /**
   * Désactive tous les feature flags (override local)
   */
  disableAllFeatureFlags(): void {
    localStorage.setItem(this.FEATURE_FLAGS_DISABLED_KEY, 'true');
    console.log('🚫 Feature flags désactivés localement');
  }

  /**
   * Réactive les feature flags (utilise PostHog)
   */
  enableFeatureFlags(): void {
    localStorage.removeItem(this.FEATURE_FLAGS_DISABLED_KEY);
    console.log('✅ Feature flags réactivés - utilisation de PostHog');
  }

  /**
   * Retourne l'état actuel de l'override
   */
  isOverrideActive(): boolean {
    return this.areFeatureFlagsDisabled();
  }

  /**
   * Bascule entre activé/désactivé
   */
  toggleFeatureFlags(): boolean {
    if (this.areFeatureFlagsDisabled()) {
      this.enableFeatureFlags();
      return false; // maintenant activé
    } else {
      this.disableAllFeatureFlags();
      return true; // maintenant désactivé
    }
  }

  /**
   * Méthode principale pour vérifier si une feature est activée
   * (affectée par l'override local)
   */
  isFeatureEnabled(featureName: string | FeatureFlags): boolean {
    // Si l'override est actif, toutes les features sont désactivées
    if (this.areFeatureFlagsDisabled()) {
      return false;
    }

    // Vérifier si PostHog est disponible
    if (!this.isPostHogAvailable()) {
      console.warn(`PostHog not available for ${featureName}`);
      return false;
    }

    // Sinon, on utilise PostHog normalement
    try {
      return posthog.isFeatureEnabled(featureName);
    } catch (error) {
      console.error(`Error checking feature ${featureName}:`, error);
      return false;
    }
  }

  /**
   * Méthode pour vérifier un feature flag INDÉPENDANT de l'override local
   * (toujours contrôlé uniquement par PostHog)
   */
  private isIndependentFeatureEnabled(featureName: string | FeatureFlags): boolean {
    // Vérifier si PostHog est disponible
    if (!this.isPostHogAvailable()) {
      console.warn(`PostHog not available for independent feature ${featureName}`);
      return false;
    }

    // Cette méthode ignore complètement l'override local
    try {
      return posthog.isFeatureEnabled(featureName);
    } catch (error) {
      console.error(`Error checking independent feature ${featureName}:`, error);
      return false;
    }
  }

  // Méthodes pour les features "classiques" (affectées par l'override)
  isTopicsContainerEnabled(): boolean {
    return this.isFeatureEnabled(FeatureFlags.DISPLAY_TOPICS);
  }

  isToursContainerEnabled(): boolean {
    return this.isFeatureEnabled(FeatureFlags.DISPLAY_TOURS);
  }

  // Méthode pour masterButton (INDÉPENDANTE de l'override)
  isMasterButtonEnabled(): boolean {
    return this.isIndependentFeatureEnabled(FeatureFlags.MASTER_BUTTON);
  }

  /**
   * Méthode utilitaire pour reset complet (utile pour les tests)
   */
  resetToDefault(): void {
    localStorage.removeItem(this.FEATURE_FLAGS_DISABLED_KEY);
    localStorage.removeItem(this.FEATURE_FLAGS_INITIALIZED_KEY);
    console.log("🔄 Feature flags reset à l'état initial");
  }
}
